import { dispatchAction } from '@ardoq/rxbeach';
import {
  saveSubmittedFeedback,
  submitFeedback as submitFeedbackAction,
  messageCancellationRequested,
  closeChatWindow,
  minimizeChatWindow,
  showChatHistory,
  hideChatHistory,
  openSnakeGame as openSnakeGameAction,
  closeSnakeGame as closeSnakeGameAction,
  openChatWindow,
  setInput as setInputAction,
  appendInput as appendInputAction,
  setCurrentScrollTop,
  setShowScrollDownButton as setShowScrollDownButtonAction,
  setAssistantDisplayMode,
  setPanelWidth as setPanelWidthAction,
  showFeedbackForm,
  badFeedbackComment,
  hideFeedbackForm,
  deleteHistoricChat as deleteHistoricChatAction,
  dismissSendMessageError as dismissSendMessageErrorAction,
  historicChatTogglePin as historicChatTogglePinAction,
  historicChatRenamed as historicChatRenamedAction,
  setHistorySearchText as setHistorySearchTextAction,
  openHistoricChat as openHistoricChatAction,
  sendMessageWithHistory,
  initializeNewChat,
  openFullscreenChatAndSend,
  openChatAndPopulate,
  setDisplayModifier,
  dismissDisplayModeHint as dismissDisplayModeHintAction,
  exportChatSession as exportChatSessionAction,
  dismissBanner as dismissBannerAction,
  requestAssistantInputFocus as requestAssistantInputFocusAction,
  setAssistantConfig as setAssistantConfigAction,
} from './actions';
import {
  AssistantCommands,
  AssistantQuestionSource,
  ChatMessageId,
  DecoratedSendMessageRequest,
} from './types';
import { AgentType, AssistantConfig, HistoricChat } from '@ardoq/api-types';
import type { AssistantDisplayMode } from './assistantStateOperations';
import type { AssistantBanner } from './assistantTypes';
import { currentTimestamp } from '@ardoq/date-time';
import { v4 as uuidv4 } from 'uuid';
import { navigationTopBarCommands } from 'navigationTopBar/navigationTopBarCommands';
import { persistSidebarState } from 'navigationTopBar/persistSidebarState';
import { navigateToManageOrganization } from 'router/navigationActions';
import { scrollToHashElement } from 'router/scrollToHashElement';
import { OrgTabs } from 'admin/manageOrganization/types';
import { CUSTOM_AGENTS_SECTION_ANCHOR_ID } from 'customAgents/customAgentsAnchorId';

const submitPositiveFeedback = ({
  messageId,
  agent,
}: {
  messageId: ChatMessageId;
  agent?: AgentType;
}) => {
  dispatchAction(submitFeedbackAction({ feedback: 'good', agent }));
  dispatchAction(saveSubmittedFeedback({ messageId, feedback: 'good' }));
  dispatchAction(hideFeedbackForm(messageId));
};

const submitNegativeFeedback = ({
  messageId,
  agent,
}: {
  messageId: ChatMessageId;
  agent?: AgentType;
}) => {
  dispatchAction(submitFeedbackAction({ feedback: 'bad', agent }));
  dispatchAction(saveSubmittedFeedback({ messageId, feedback: 'bad' }));
  dispatchAction(showFeedbackForm(messageId));
};

const sendWithHistory = (payload: DecoratedSendMessageRequest) =>
  dispatchAction(sendMessageWithHistory(payload));

const cancel = () => dispatchAction(messageCancellationRequested());

const close = () => dispatchAction(closeChatWindow());
const startNewChat = () => dispatchAction(initializeNewChat());
const showHistory = () => dispatchAction(showChatHistory());
const hideHistory = () => dispatchAction(hideChatHistory());

const openSnakeGame = () => dispatchAction(openSnakeGameAction());
const closeSnakeGame = () => dispatchAction(closeSnakeGameAction());

const minimize = () =>
  dispatchAction(
    minimizeChatWindow({
      silenceEvent: false,
    })
  );

const setDisplayMode = (mode: AssistantDisplayMode) =>
  dispatchAction(setAssistantDisplayMode(mode));

const setPanelWidth = (width: number) =>
  dispatchAction(setPanelWidthAction(width));

const open = () => dispatchAction(openChatWindow());

const setInput = (value: string) => dispatchAction(setInputAction(value));

const appendInput = (value: string) => dispatchAction(appendInputAction(value));

const setScrollTop = (value: number) =>
  dispatchAction(setCurrentScrollTop(value));

const setShowScrollDownButton = (value: boolean) =>
  dispatchAction(setShowScrollDownButtonAction(value));

const submitFeedback = ({
  messageId,
  feedback,
  agent,
}: {
  messageId: ChatMessageId;
  feedback: string;
  agent?: AgentType;
}) => {
  if (feedback.length === 0) return;

  dispatchAction(
    badFeedbackComment({
      agent,
      messageId,
      feedback,
    })
  );
};

const dismissSendMessageError = () =>
  dispatchAction(dismissSendMessageErrorAction());

const historicChatTogglePin = (chat: HistoricChat) =>
  dispatchAction(historicChatTogglePinAction(chat));

const renameHistoricChat = (chat: HistoricChat, title: string) =>
  dispatchAction(historicChatRenamedAction({ chat, title }));

const deleteHistoricChat = (id: string) =>
  dispatchAction(deleteHistoricChatAction(id));

const setHistorySearchText = (text: string) => {
  dispatchAction(setHistorySearchTextAction(text));
};

const openHistoricChat = (chat: HistoricChat) => {
  dispatchAction(openHistoricChatAction(chat));
};

const openFullscreenAndSend = (
  content: string,
  source: AssistantQuestionSource
): void =>
  dispatchAction(
    openFullscreenChatAndSend({
      content,
      source,
      chatSessionId: null,
      timestamp: currentTimestamp(),
      clientRequestId: uuidv4(),
    })
  );

const openAndSend = (
  content: string,
  source: AssistantQuestionSource
): void => {
  dispatchAction(openChatWindow());
  dispatchAction(
    sendMessageWithHistory({
      content,
      source,
      chatSessionId: null,
      timestamp: currentTimestamp(),
      clientRequestId: uuidv4(),
    })
  );
};

const openAndPopulate = (content: string): void =>
  dispatchAction(openChatAndPopulate({ content }));

const expandFullscreenWidth = () =>
  dispatchAction(setDisplayModifier('expanded'));

const shrinkFullscreenWidth = () =>
  dispatchAction(setDisplayModifier('default'));

const dismissDisplayModeHint = () =>
  dispatchAction(dismissDisplayModeHintAction());

const navigateToManageAgents = () => {
  dispatchAction(
    navigateToManageOrganization({ manageOrgTab: OrgTabs.AI_SETTINGS })
  );
  scrollToHashElement(`#${CUSTOM_AGENTS_SECTION_ANCHOR_ID}`, {
    behavior: 'smooth',
  });
};

const exportChatSession = (chatSessionId: string) =>
  dispatchAction(exportChatSessionAction(chatSessionId));

const openMainNav = () => {
  persistSidebarState('open');
  navigationTopBarCommands.openSidebar({
    event: 'TopBar0 Toggled sidebar',
    payload: { state: 'open' },
  });
};

const dismissBanner = ({ id, source }: AssistantBanner) => {
  dispatchAction(dismissBannerAction({ bannerId: id, source }));
};

const setAssistantConfig = (config: AssistantConfig) => {
  dispatchAction(setAssistantConfigAction(config));
};

const requestAssistantInputFocus = (requesting: boolean) => {
  dispatchAction(requestAssistantInputFocusAction(requesting));
};

export const commands = {
  submitPositiveFeedback,
  submitNegativeFeedback,
  sendWithHistory,
  cancel,
  close,
  startNewChat,
  minimize,
  setDisplayMode,
  setPanelWidth,
  open,
  setInput,
  appendInput,
  setScrollTop,
  setShowScrollDownButton,
  submitFeedback,
  dismissSendMessageError,
  showHistory,
  hideHistory,
  openSnakeGame,
  closeSnakeGame,
  historicChatTogglePin,
  renameHistoricChat,
  deleteHistoricChat,
  setHistorySearchText,
  openHistoricChat,
  openFullscreenAndSend,
  openAndSend,
  openAndPopulate,
  expandFullscreenWidth,
  shrinkFullscreenWidth,
  dismissDisplayModeHint,
  openMainNav,
  navigateToManageAgents,
  exportChatSession,
  dismissBanner,
  requestAssistantInputFocus,
  setAssistantConfig,
} satisfies AssistantCommands;
